import axios from 'axios';
import { useEffect, useState } from 'react';

import { getEnv } from '@ctct/ctct-helpers';

import { getCountryCode } from '../../../api/DistUI';

const SMS_REGISTRY_BASE_URL = `https://app${getEnv()}constantcontact.com/v1/sms_registrations/orgs/CTCT`;

/** Normalize billing/distui country codes to ISO codes expected by sms_registrations. */
const normalizeCountryCode = (countryCode: string): string => {
  const normalized = countryCode.toUpperCase();

  return normalized === 'UK' ? 'GB' : normalized;
};

/**
 * Mirrors @ctct/sms-utils/src/js/api/SmsRegistry.getCountryEligibility — plain axios with
 * credentials, no CSRF headers. The shared axiosConfig instance is not used here because it
 * attaches headers/interceptors that break this endpoint.
 */
const getCountryEligibility = async (countryCode: string) => {
  const response = await axios.get(`${SMS_REGISTRY_BASE_URL}/country_eligibility/${countryCode}`, {
    withCredentials: true,
  });

  return response.data;
};

interface UseSmsEligibilityOptions {
  /** When false, skips the network call (e.g. until add-on catalog has loaded). */
  enabled?: boolean;
}

/**
 * Checks SMS Marketing eligibility for the customer's country via the SMS registry endpoint.
 * Mirrors apps/feature-upgrade-dialog useSmsEligibility without domain-layer / state-server-layer.
 *
 * @param countryCodeOverride - Optional ISO country code (e.g. from billing info)
 */
export const useSmsEligibility = (countryCodeOverride?: string, options: UseSmsEligibilityOptions = {}) => {
  const { enabled = true } = options;
  const [isEligible, setIsEligible] = useState(false);
  const [isLoading, setIsLoading] = useState(enabled);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(true);
      setHasError(false);

      return undefined;
    }

    let cancelled = false;

    async function checkEligibility() {
      setIsLoading(true);
      setHasError(false);

      try {
        const rawCountryCode = countryCodeOverride ?? (await getCountryCode());

        if (!rawCountryCode) {
          if (!cancelled) {
            setIsEligible(false);
            setIsLoading(false);
          }

          return;
        }

        const countryCode = normalizeCountryCode(rawCountryCode);
        const response = await getCountryEligibility(countryCode);

        if (!cancelled) {
          setIsEligible(response.is_provided_country_eligible);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setIsEligible(false);
          setHasError(true);
          setIsLoading(false);
        }
      }
    }

    checkEligibility();

    return () => {
      cancelled = true;
    };
  }, [countryCodeOverride, enabled]);

  return { isEligible, isLoading, hasError };
};
